import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Folder, Eye, Monitor, Smartphone } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

/**
 * Desktop-First IDE Layout
 * 
 * INVARIANTS:
 * - Left panel (Workspace) ALWAYS exists
 * - Center surface (Workbench) ALWAYS exists  
 * - Right panel (Inspector/Context) ALWAYS exists
 * 
 * Panels may be collapsed to thin rails but are NEVER conditionally not rendered.
 * Mobile shows a friendly message directing users to desktop.
 */

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Mobile fallback component
const MobileFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-950 to-slate-900 flex items-center justify-center p-6">
    <div className="max-w-md w-full text-center space-y-6">
      <div className="flex justify-center">
        <div className="relative">
          <Monitor className="w-20 h-20 text-blue-400" />
          <Smartphone className="w-8 h-8 text-gray-500 absolute -bottom-1 -right-1 line-through" />
        </div>
      </div>
      
      <div className="space-y-3">
        <h1 className="text-2xl font-bold text-white">Desktop Experience Required</h1>
        <p className="text-gray-400">
          FIRMASTATE is a powerful IDE designed for desktop use. 
          The 3-panel layout and graph editing tools require a larger screen.
        </p>
      </div>
      
      <div className="bg-black/40 border border-white/10 rounded-lg p-4 text-left space-y-2">
        <p className="text-sm font-medium text-gray-300">Minimum Requirements:</p>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Screen width: 1024px or wider</li>
          <li>• Recommended: Desktop or laptop computer</li>
          <li>• Best experience: 1440px+ displays</li>
        </ul>
      </div>
      
      <div className="pt-4">
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </div>
      
      <p className="text-xs text-gray-500">
        FIRMASTATE • Desktop IDE
      </p>
    </div>
  </div>
);

const DockLayout = ({ 
  leftDock, 
  centerSurface, 
  rightDock,
  topBar,
  loginButton,
  leftDockOpen = true,
  rightDockOpen = true,
  onLeftDockToggle,
  onRightDockToggle,
  focusMode = false
}) => {
  const isMobile = useIsMobile();
  
  // Show mobile fallback for screens < 1024px
  if (isMobile) {
    return <MobileFallback />;
  }
  // Focus mode: panels collapse but toolbar remains
  if (focusMode) {
    return (
      <div className="h-screen flex flex-col bg-[#0a0e27]">
        <div className="flex items-center justify-between">
          <div className="flex-1">{topBar}</div>
          <div className="pr-4">{loginButton}</div>
        </div>
        <div className="flex-1 flex overflow-hidden">
          {/* Collapsed Left Rail */}
          <CollapsedRail 
            side="left" 
            icon={<Folder className="w-4 h-4" />}
            tooltip="Workspace (W)"
            onClick={onLeftDockToggle}
          />
          
          {/* Center takes full space in focus mode */}
          <div className="flex-1 overflow-hidden">
            {centerSurface}
          </div>
          
          {/* Collapsed Right Rail */}
          <CollapsedRail 
            side="right" 
            icon={<Eye className="w-4 h-4" />}
            tooltip="Inspector (I)"
            onClick={onRightDockToggle}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0a0e27]" data-testid="dock-layout">
      <div className="flex items-center justify-between">
        <div className="flex-1">{topBar}</div>
        <div className="pr-4">{loginButton}</div>
      </div>
      
      {/* CSS Grid-based 3-Panel Layout */}
      <div 
        className="flex-1 overflow-hidden"
        style={{
          display: 'grid',
          gridTemplateColumns: `${leftDockOpen ? '280px' : '40px'} 1fr ${rightDockOpen ? '360px' : '40px'}`,
          transition: 'grid-template-columns 200ms ease-in-out'
        }}
        data-testid="panel-grid"
      >
        {/* LEFT PANEL - Workspace (Always Rendered) */}
        <div 
          className={cn(
            "border-r border-white/10 bg-black/20 backdrop-blur-sm overflow-hidden",
            "transition-all duration-200"
          )}
          data-testid="left-dock"
        >
          {leftDockOpen ? (
            <div className="h-full flex flex-col">
              {/* Panel Header with Collapse Button */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/30">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Workspace</span>
                <button 
                  onClick={onLeftDockToggle}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  title="Collapse (W)"
                  data-testid="collapse-left"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              {/* Panel Content */}
              <div className="flex-1 overflow-auto">
                {leftDock}
              </div>
            </div>
          ) : (
            <CollapsedRail 
              side="left" 
              icon={<Folder className="w-4 h-4" />}
              tooltip="Workspace (W)"
              onClick={onLeftDockToggle}
              expanded
            />
          )}
        </div>

        {/* CENTER SURFACE - Graph Workbench (Always Rendered) */}
        <div className="overflow-hidden flex flex-col" data-testid="center-surface-container">
          {centerSurface}
        </div>

        {/* RIGHT PANEL - Inspector Stack (Always Rendered) */}
        <div 
          className={cn(
            "border-l border-white/10 bg-black/20 backdrop-blur-sm overflow-hidden",
            "transition-all duration-200"
          )}
          data-testid="right-dock"
        >
          {rightDockOpen ? (
            <div className="h-full flex flex-col">
              {/* Panel Header with Collapse Button */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-black/30">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Inspector</span>
                <button 
                  onClick={onRightDockToggle}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  title="Collapse (I)"
                  data-testid="collapse-right"
                >
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              {/* Panel Content */}
              <div className="flex-1 overflow-auto">
                {rightDock}
              </div>
            </div>
          ) : (
            <CollapsedRail 
              side="right" 
              icon={<Eye className="w-4 h-4" />}
              tooltip="Inspector (I)"
              onClick={onRightDockToggle}
              expanded
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Collapsed Panel Rail
 * Shows a thin rail with icon + tooltip when panel is collapsed.
 * This guarantees tools feel latent, not missing.
 */
const CollapsedRail = ({ side, icon, tooltip, onClick, expanded = false }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={cn(
              "h-full flex items-center justify-center",
              "hover:bg-white/5 transition-colors",
              expanded ? "w-full" : "w-10 flex-shrink-0"
            )}
            data-testid={`rail-${side}`}
          >
            <div className={cn(
              "flex flex-col items-center gap-2 text-gray-500 hover:text-gray-300",
              expanded && "py-4"
            )}>
              {icon}
              {side === 'left' ? (
                <ChevronRight className="w-3 h-3" />
              ) : (
                <ChevronLeft className="w-3 h-3" />
              )}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent side={side === 'left' ? 'right' : 'left'}>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DockLayout;
